import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  ChevronLeft,
  Play,
  Globe,
  RefreshCw,
  Rocket,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { apiClient } from '@/services/api';
import { authService } from '@/services/auth';
import { useApplicationWebSocket } from '@/hooks/useWebSocket';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const STUDIO_URL = import.meta.env.VITE_STUDIO_URL || 'http://localhost:5174';

export default function VisualBuilder() {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [studioReady, setStudioReady] = useState(false);

  // WebSocket for real-time build/deploy updates
  const { buildStatus, deploymentStatus, logs } = useApplicationWebSocket(applicationId || '');

  // Fetch application data
  const { data: application, isLoading } = useQuery({
    queryKey: ['application', applicationId],
    queryFn: () => apiClient.getApplication(applicationId!),
    enabled: !!applicationId,
  });

  // Deploy mutation
  const deployMutation = useMutation({
    mutationFn: () => apiClient.deployApplication(applicationId!),
    onSuccess: () => {
      toast.success('Deployment started');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start deployment');
    },
  });

  // Send auth token and config to studio iframe once it's ready
  const sendAuthToStudio = useCallback(() => {
    if (!iframeRef.current?.contentWindow) return;
    const token = authService.getToken();
    if (!token) return;

    iframeRef.current.contentWindow.postMessage(
      {
        type: 'zewst:auth',
        token,
        apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:3000/api',
      },
      STUDIO_URL
    );
  }, []);

  // Listen for messages from studio iframe
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      // Only accept messages from the studio origin
      if (event.origin !== new URL(STUDIO_URL).origin) return;

      const { type, ...data } = event.data || {};

      switch (type) {
        case 'zewst:studio-ready':
          setStudioReady(true);
          sendAuthToStudio();
          break;
        case 'zewst:request-auth':
          sendAuthToStudio();
          break;
        case 'zewst:deploy':
          deployMutation.mutate();
          break;
        case 'zewst:navigate-back':
          navigate('/applications');
          break;
        case 'zewst:toast':
          if (data.variant === 'error') toast.error(data.message);
          else toast.success(data.message);
          break;
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [sendAuthToStudio, navigate, deployMutation]);

  // Re-send auth if token changes
  useEffect(() => {
    if (studioReady) sendAuthToStudio();
  }, [studioReady, sendAuthToStudio]);

  // Forward build/deploy status to studio
  useEffect(() => {
    if (!studioReady || !iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage(
      {
        type: 'zewst:build-status',
        buildStatus,
        deploymentStatus,
      },
      STUDIO_URL
    );
  }, [buildStatus, deploymentStatus, studioReady]);

  const studioSrc = `${STUDIO_URL}/project/${applicationId}`;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading builder...</p>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Rocket className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Application not found</h2>
          <p className="text-muted-foreground mb-4">
            The application you're looking for doesn't exist or you don't have access.
          </p>
          <Button onClick={() => navigate('/applications')}>
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Applications
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header Bar */}
      <div className="border-b px-4 py-2 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/applications')}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-lg font-semibold">{application.name}</h1>
          <Badge
            variant={
              application.status === 'deployed' || (application.status as string) === 'DEPLOYED'
                ? 'default'
                : 'secondary'
            }
          >
            {application.status}
          </Badge>
        </div>

        <div className="flex items-center gap-2">
          {/* Build/deploy status indicators */}
          {buildStatus !== 'idle' && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground mr-2">
              <RefreshCw
                className={`h-3.5 w-3.5 ${buildStatus === 'running' ? 'animate-spin' : ''}`}
              />
              <span>Build: {buildStatus}</span>
            </div>
          )}
          {deploymentStatus !== 'idle' && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground mr-2">
              <Globe className="h-3.5 w-3.5" />
              <span>Deploy: {deploymentStatus}</span>
            </div>
          )}

          {/* Live URL link */}
          {application.liveUrl && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(application.liveUrl, '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              Live Site
            </Button>
          )}

          {/* Deploy button */}
          <Button
            size="sm"
            onClick={() => deployMutation.mutate()}
            disabled={deployMutation.isPending}
          >
            <Play className="h-4 w-4 mr-1" />
            {deployMutation.isPending ? 'Deploying...' : 'Deploy'}
          </Button>
        </div>
      </div>

      {/* Studio Iframe */}
      <div className="flex-1 relative">
        {!studioReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-background z-10">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Loading studio...</p>
            </div>
          </div>
        )}
        <iframe
          ref={iframeRef}
          src={studioSrc}
          className="w-full h-full border-0"
          title="Zewst Studio"
          allow="clipboard-read; clipboard-write"
          onLoad={() => {
            // Studio may not send zewst:studio-ready if it doesn't have the listener yet.
            // Give it a moment then send auth proactively.
            setTimeout(() => {
              if (!studioReady) {
                setStudioReady(true);
                sendAuthToStudio();
              }
            }, 2000);
          }}
        />
      </div>
    </div>
  );
}
